export function avatarUploader(opts = {}) {
    // Do NOT destructure at the function boundary — we need lazy reads.
    const cfg = {
        value: '',                     // can be a string OR a function () => string
        folder: 'players',
        targetW: 600,
        targetH: 600,
        format: 'image/webp',
        quality: 0.9,
        aspect: 1,
        maxBytes: 800 * 1024, // 500 Kb limit
        placeHolder: '/assets/placeholder-team.png',
        onDone: (url) => { },
        ...opts,
    };

    const getInitialValue = () => {
        try { return typeof cfg.value === 'function' ? (cfg.value() || '') : (cfg.value || ''); }
        catch { return ''; }
    };

    return {

        url: '',

        /** state */
        previewUrl: '',
        uploading: false,
        progress: 0,
        error: '',
        dragOver: false,
        hint: computeAspectHint(cfg.aspect),
        placeHolder:  '/assets/placeholder-team.png',

        /** (optional) expose init so Alpine can call it */
        init() {

            if(cfg.placeHolder) this.placeHolder = cfg.placeHolder;

            // seed initial
            const v = getInitialValue();
            if (v) { this.url = v; this.previewUrl = v; }

            // keep preview + parent in sync
            this.$watch('url', (v) => {
                this.previewUrl = v || '';
                cfg.onDone?.(this.previewUrl);
            });

            // 🔔 Tell parent we are ready and expose safe setters/getters
            this.$root.dispatchEvent(new CustomEvent('avatar-uploader:ready', {
                detail: {
                    setUrl: (u) => this.setUrl(u),
                    getUrl: () => this.url
                },
                bubbles: true
            }));
        },

        // Make external setter set the *model* (so parent sees it too)
        setUrl(url) {
            this.url = url || '';
        },

        clear() { this.setUrl(''); },

        /** pick file from input */
        async pick(e) {
            const file = e.target.files?.[0];
            if (!file) return;
            await this.handleFile(file);
            e.target.value = '';
        },

        /** drag-drop */
        async drop(e) {
            this.dragOver = false;
            const file = e.dataTransfer?.files?.[0];
            if (!file) return;
            await this.handleFile(file);
        },

        /** paste from clipboard */
        async paste(e) {
            const items = e.clipboardData?.items || [];
            for (const it of items) {
                if (it.type?.startsWith?.('image/')) {
                    const file = it.getAsFile();
                    if (file) await this.handleFile(file);
                    break;
                }
            }
        },

        async handleFile(file) {
            try {
                this.error = '';

                if (file.size > cfg.maxBytes) {
                    this.error = `Image is too large (${Math.round(file.size / 1024)} KB). Max ${Math.round(cfg.maxBytes / 1024)} KB.`;
                    return;
                }

                const processed = await this.processImage(file);

                if (processed.size > cfg.maxBytes) {
                    this.error = `Image is too large after processing (${Math.round(processed.size / 1024)} KB). `
                        + `Max ${Math.round(cfg.maxBytes / 1024)} KB. Try a simpler image or lower quality.`;
                    return;
                }

                const url = await this.upload(processed);
                this.setUrl(url);     // updates preview + notifies parent
            } catch (err) {
                console.error(err);
                this.error = 'Upload failed. Please try another image.';
                this.uploading = false;
            }
        },

        /** client-side resize/convert using canvas */
        async processImage(file) {
            const img = await this.readImage(file);
            const { w, h } = fitWithin(img.width, img.height, cfg.targetW, cfg.targetH);

            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, w, h);

            // Some browsers can return null if format unsupported; fallback to JPEG.
            const blob = await new Promise((res, rej) => {
                try {
                    canvas.toBlob((b) => {
                        if (b) { res(b); return; }
                        // fallback
                        canvas.toBlob((b2) => b2 ? res(b2) : rej(new Error('Canvas toBlob failed')),
                            'image/jpeg', 0.9);
                    }, cfg.format, cfg.quality);
                } catch (e) { rej(e); }
            });

            const ext =
                cfg.format.endsWith('webp') ? 'webp' :
                    cfg.format.endsWith('png') ? 'png' :
                        cfg.format.endsWith('jpeg') ? 'jpeg' : 'jpg';

            return new File([blob], `image.${ext}`, { type: blob.type || cfg.format });
        },

        readImage(file) {
            return new Promise((resolve, reject) => {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
                img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
                img.src = url;
            });
        },

        /** ask API for a presigned URL, then PUT */
        async upload(file) {
            this.uploading = true;
            this.progress = 5;

            const fd = new FormData();
            fd.append('file', file, file.name);
            fd.append('folder', cfg.folder);
            fd.append('fileExt', file.name.split('.').pop());

            const url = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', '/api/uploads/direct');
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) this.progress = Math.round((e.loaded / e.total) * 100);
                };
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try { resolve(JSON.parse(xhr.responseText).publicUrl); } catch { reject(new Error('Bad JSON')); }
                    } else reject(new Error('Server upload failed'));
                };
                xhr.onerror = () => reject(new Error('Network error to API'));
                xhr.send(fd);
            });

            this.uploading = false; this.progress = 100;
            return url;
        }
    };

    /** helpers */
    function fitWithin(w, h, maxW, maxH) {
        if (!maxW && !maxH) return { w, h };
        const rw = maxW ? maxW / w : Infinity;
        const rh = maxH ? maxH / h : Infinity;
        const r = Math.min(rw, rh);
        return { w: Math.max(1, Math.round(w * r)), h: Math.max(1, Math.round(h * r)) };
    }

    function computeAspectHint(a) {
        if (!a) return 'Any aspect ok';
        if (typeof a === 'string') {
            const parts = a.split('/');
            if (parts.length === 2) {
                const A = Number(parts[0]), B = Number(parts[1]);
                if (A > 0 && B > 0) return `Suggested aspect ${A}:${B}`;
            }
            const n = Number(a);
            if (Number.isFinite(n)) return computeAspectHint(n);
            return 'Any aspect ok';
        }
        if (Number.isFinite(a)) {
            const den = 1000;
            const num = Math.round(a * den);
            const g = gcd(Math.abs(num), den);
            const A = Math.max(1, Math.round(num / g));
            const B = Math.max(1, Math.round(den / g));
            return `Suggested aspect ${A}:${B}`;
        }
        return 'Any aspect ok';
    }
    function gcd(a, b) { while (b) [a, b] = [b, a % b]; return a || 1; }
}
